import {FC, Fragment, useContext, useEffect, useMemo, useRef, useState} from "react";
import {
    BookmakerData,
    EventMarketCap,
    LiveMarketData,
    LiveMarketObject,
    LiveMarketSpreads
} from "../../../@types/response";
import {useSelector} from "react-redux";
import {getBookmakers} from "../../../store/selectors/getBookmakers";
import {getLiveMarkets} from "../../../store/selectors/getLiveMarkets";
import {getLiveBooks} from "../../../store/selectors/live/getBooks";
import {getBetfairUrl} from '../../../store/utils/events'
import {BETFAIR, PINNACLE} from "../../../constants/CommonConstants";
import {MODALS_TYPES} from "../../../constants/ModalsConstants";
import {ModalsContext} from "../modals/ModalsContext";
import {EventModalProps} from "../../../@types/inputs";

interface ModalLiveTableProps {
    odds: any;
    event: EventModalProps | null;
    highlightBest?: boolean;
    refs?: EventMarketCap;
}

export const ModalLiveTable: FC<ModalLiveTableProps> = ({odds, event = null, highlightBest = true, refs}) => {
    const bookmakers: BookmakerData = useSelector(getBookmakers);
    const allMarkets: LiveMarketData = useSelector(getLiveMarkets);
    const enabledBooks: number[] = useSelector(getLiveBooks);

    const {setOpenedModalsCallback, setModalsDataCallback} = useContext(ModalsContext);
    const [bestBookmakers, setBestBookmakers]: any = useState({});
    const [spreads, setSpreads] = useState<LiveMarketSpreads>({});
    const [tooltip, setTooltip] = useState<null | string>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({top: 0, left: 0});
    const [tooltipTimeout, setTooltipTimeout] = useState<any>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const markets: LiveMarketObject[] = useMemo(() => {
        const eventSportId = event?.sportId;
        return Object.values(allMarkets).filter((market) => eventSportId == null || market.sport === eventSportId);
    }, [allMarkets, event?.sportId]);

    const splitLiveMarketKey = (marketKey: string): { marketId: string, sbv: string } => {
        if (!marketKey.includes('@')) return {marketId: marketKey, sbv: '#'}
        const [marketId, ...sbvParts] = marketKey.split('@')
        return {marketId, sbv: sbvParts.join('@') || '#'}
    }

    const getLiveMarketKey = (marketId: string, sbv: string): string => {
        return sbv !== '#' ? `${marketId}@${sbv}` : marketId
    }

    const getMarketSpreads = (market: any): { [sbv: string]: any } => {
        if (!market) return {}
        if (market.sign) return {'#': market.sign}
        return Object.entries(market)
                .filter(([, value]: [string, any]) => value?.sign)
                .reduce((obj, [sbv, value]: [string, any]) => ({...obj, [sbv]: value.sign}), {})
    }

    const getBookmakerMarketSpreads = (bookmakerMarkets: any, marketId: string): { [sbv: string]: any } => {
        const result = {...getMarketSpreads(bookmakerMarkets?.[marketId])}
        Object.entries(bookmakerMarkets || {}).forEach(([marketKey, market]: [string, any]) => {
            const parsed = splitLiveMarketKey(marketKey)
            if (parsed.marketId === marketId && parsed.sbv !== '#' && market?.sign) {
                result[parsed.sbv] = market.sign
            }
        })
        return result
    }

    const getMainSbv = (bookmakerMarkets: any, marketId: string): string => {
        const marketSpreads = getBookmakerMarketSpreads(bookmakerMarkets, marketId)
        return Object.entries(marketSpreads)
                .map(([sbv, signs]: [string, any]) => {
                    const values = Object.values(signs)
                            .map((sign: any) => sign?.backOdd ?? 0)
                            .filter((odd: number) => odd > 1)
                    const distance = values.reduce((min: number, odd: number) => Math.min(min, Math.abs(odd - 1.85)), Number.POSITIVE_INFINITY)
                    return {sbv, distance}
                })
                .sort((a, b) => a.distance - b.distance)[0]?.sbv ?? '#'
    }

    const getVisibleSigns = (bookmakerMarkets: any, marketId: string) => {
        const sbv = getMainSbv(bookmakerMarkets, marketId)
        const marketSpreads = getBookmakerMarketSpreads(bookmakerMarkets, marketId)
        return {sbv, signs: marketSpreads[sbv] || {}, multiple: Object.keys(marketSpreads).length > 1}
    }

    const normalizeLiveSpreads = (bookmakerMarkets: any, marketId: string): LiveMarketSpreads => {
        return Object.entries(getBookmakerMarketSpreads(bookmakerMarkets, marketId))
                .reduce((obj, [sbv, signs]: [string, any]) => ({
                    ...obj,
                    [sbv]: Object.entries(signs)
                            .reduce((signsObj, [signId, sign]: [string, any]) => ({
                                ...signsObj,
                                [signId]: sign?.backOdd ?? sign?.odd ?? sign
                            }), {})
                }), {})
    }

    const showTooltip = (bookmakerId: number, marketId: string, signId: string) => {
        setTooltip(`${bookmakerId}#${marketId}_${signId}`)
    }

    const updateTooltipPosition = (event: React.MouseEvent<HTMLElement>) => {
        const tooltipWidth = tooltipRef.current?.offsetWidth || 260
        const tooltipHeight = tooltipRef.current?.offsetHeight || 180
        const maxLeft = window.innerWidth - tooltipWidth - 10
        const maxTop = window.innerHeight - tooltipHeight - 10

        setTooltipPosition({
            left: Math.max(10, Math.min(event.clientX + 12, maxLeft)),
            top: Math.max(10, Math.min(event.clientY + 12, maxTop))
        })
    }

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, bookmakerId: number, marketId: string, signId: string, bookmakerMarkets: any) => {
        if (tooltip) setTooltip(null)
        setSpreads(normalizeLiveSpreads(bookmakerMarkets, marketId))
        updateTooltipPosition(event)
        setTooltipTimeout(setTimeout(showTooltip, 500, bookmakerId, marketId, signId))
    }

    function handleMouseLeave() {
        setTooltip(null)
        setSpreads({})
        if (tooltipTimeout) {
            clearTimeout(tooltipTimeout)
            setTooltipTimeout(null)
        }
    }

    const getSortedOutcomeIds = (outcomes: LiveMarketObject["outcomes"]) =>
            Object.keys(outcomes).sort((a, b) => {
                const pa = outcomes[a]?.priority ?? Infinity;
                const pb = outcomes[b]?.priority ?? Infinity;
                if (pa !== pb) return pa - pb;
                const na = +a;
                const nb = +b;
                if (!Number.isNaN(na) && !Number.isNaN(nb)) {
                    return na - nb;
                }
                return a.localeCompare(b);
            });

    const orderedBooks = enabledBooks
            .map((id, index) => ({id, index}))
            .sort((a, b) => {
                if (a.id === BETFAIR) return -1;
                if (b.id === BETFAIR) return 1;
                return a.index - b.index;
            })
            .map((item) => item.id);

    useEffect(() => {
        if (!markets.length || !Object.keys(odds).length) {
            setBestBookmakers({})
            return
        }

        let bests: any = {}
        for (const market of markets) {
            bests[market.id.toString()] = {}
            for (const signId in market.outcomes) {
                let max = 0
                for (const bookmakerId in bookmakers) {
                    if (bookmakers[bookmakerId].isComparison || bookmakers[bookmakerId].details < 0) continue
                    const {signs} = getVisibleSigns(odds[bookmakerId]?.['markets'], market.id)
                    const backOdd = signs?.[signId]?.['backOdd'] || 0
                    if (backOdd > max) max = backOdd
                }
                bests[market.id.toString()][signId] = max
            }
        }
        setBestBookmakers(bests)

    }, [markets, odds, bookmakers])

    return (
            <table className="min-w-[1400px] w-full border-collapse border-spacing-0 text-[12px] font-semibold text-slate-100">
                <thead className="bg-slate-950">
                <tr className="border-b border-slate-800 text-slate-300">
                    <th
                            rowSpan={2}
                            className="sticky left-0 z-20 min-w-[150px] border-r border-slate-800 bg-slate-950 px-2.5 py-2 text-left font-semibold"
                    >
                        Bookmaker
                    </th>
                    {markets.map((market, marketIndex) => {
                        const outcomeIds = getSortedOutcomeIds(market.outcomes);
                        const isLastMarket = marketIndex === markets.length - 1;
                        return (
                                <th
                                        key={`market-${market.id}`}
                                        colSpan={outcomeIds.length}
                                        className={`${!isLastMarket ? "border-r border-slate-800" : ""} px-2 py-1.5 text-center font-semibold`}
                                >
                                    {market.name}
                                </th>
                        );
                    })}
                </tr>
                <tr className="border-b border-slate-800 text-slate-400">
                    {markets.flatMap((market, marketIndex) => {
                        const outcomeIds = getSortedOutcomeIds(market.outcomes);
                        const isLastMarket = marketIndex === markets.length - 1;
                        return outcomeIds.map((signId, signIndex) => {
                            const isLastOutcome = signIndex === outcomeIds.length - 1;
                            const border = isLastOutcome && !isLastMarket ? "border-r border-slate-800" : "";
                            return (
                                    <th
                                            key={`sign-${market.id}-${signId}`}
                                            className={`${border} px-1.5 py-1.5 text-center font-semibold`}
                                    >
                                        {market.outcomes[signId].name}
                                    </th>
                            );
                        });
                    })}
                </tr>
                </thead>

                <tbody className="divide-y divide-slate-800">
                {orderedBooks.map((order_id) => {
                    if (!Object.keys(bookmakers).includes(order_id.toString())) return null
                    const bookmaker = bookmakers[order_id]
                    const isBetfairRow = bookmaker.id === BETFAIR;
                    const rowClass = isBetfairRow ? "bg-sky-900/40" : "hover:bg-slate-900/60";
                    const stickyBg = isBetfairRow ? "bg-sky-900" : "bg-slate-950";
                    const emptyColor = isBetfairRow ? "text-slate-300" : "text-slate-400";

                    return (
                            <tr key={`book-${bookmaker.id}`} className={rowClass}>
                                <td className={`sticky left-0 z-30 ${stickyBg} px-2.5 py-2 font-bold text-white`}>
                                    {isBetfairRow ? (
                                            <div className="flex items-center gap-2">
                                                <span
                                                        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-500/30 text-xs text-sky-300">★</span>
                                                <span>{bookmaker.name}</span>
                                            </div>
                                    ) : (
                                            bookmaker.name
                                    )}
                                </td>
                                {markets.flatMap((marketItem, marketIndex) => {
                                    const bookmakerMarkets = odds[bookmaker.id]?.markets
                                    const visibleMarket = getVisibleSigns(bookmakerMarkets, marketItem.id.toString())
                                    const outcomeIds = getSortedOutcomeIds(marketItem.outcomes);
                                    const isLastMarket = marketIndex === markets.length - 1;

                                    return outcomeIds.map((signId, signIndex) => {
                                        const isLastOutcome = signIndex === outcomeIds.length - 1;
                                        const border = isLastOutcome && !isLastMarket ? "border-r border-slate-800" : "";

                                        const backOdd = visibleMarket.signs?.[signId]?.backOdd
                                        const top = bestBookmakers[marketItem.id.toString()]?.[signId] ?? 0
                                        const isTop =
                                                highlightBest &&
                                                !bookmaker.isComparison &&
                                                backOdd != null &&
                                                backOdd >= top &&
                                                backOdd > 1;

                                        const marketRef = refs?.[getLiveMarketKey(marketItem.id.toString(), visibleMarket.sbv)] ?? refs?.[marketItem.id]
                                        const betfairUrl = getBetfairUrl(
                                                marketRef?.bMarketId?.toString(),
                                                marketRef?.[signId]?.toString()
                                        )

                                        const onOddClick =
                                                bookmaker.id === BETFAIR ? () => {
                                                    betfairUrl && window.open(betfairUrl, "betfair", "width=925,height=660")
                                                } : (bookmaker.id === PINNACLE && event) ? () => {
                                                    setModalsDataCallback(MODALS_TYPES.SIGN_CHART_MODAL, {
                                                        odd: {
                                                            eventId: event.id,
                                                            bookmakerId: PINNACLE,
                                                            marketId: marketItem.id.toString(),
                                                            spread: visibleMarket.sbv,
                                                            signId: signId,
                                                        },
                                                        event: {
                                                            name: event.name,
                                                            id: event.id
                                                        }
                                                    });
                                                    setOpenedModalsCallback(MODALS_TYPES.SIGN_CHART_MODAL, true);
                                                } : null;

                                        const isClickable = !!onOddClick;
                                        const value = backOdd > 1 ? backOdd : null;
                                        const valueNode = value != null ? value : "-";

                                        return (
                                                <td key={`cell-${bookmaker.id}-${marketItem.id}-${signId}`}
                                                    className={`${border} px-1.5 py-2 text-center`}
                                                >
                                                    {visibleMarket.sbv !== '#' ? (
                                                            <Fragment>
                                                                <span
                                                                        className={`mr-1 text-xs font-thin ${visibleMarket.multiple ? 'cursor-pointer text-sky-300' : 'text-slate-300'}`}
                                                                        onMouseEnter={(event) => {
                                                                            if (!visibleMarket.multiple) return
                                                                            handleMouseEnter(event, bookmaker.id, marketItem.id, signId, bookmakerMarkets)
                                                                        }}
                                                                        onMouseMove={updateTooltipPosition}
                                                                        onMouseLeave={handleMouseLeave}
                                                                >
                                                                    [{visibleMarket.sbv}]
                                                                </span>
                                                                {tooltip === `${bookmaker.id}#${marketItem.id}_${signId}` ? (
                                                                        <div
                                                                                ref={tooltipRef}
                                                                                className='fixed p-4 z-50 bg-eventDetail-sbv text-white px-4 py-2 rounded-xl font-light text-xs'
                                                                                style={{
                                                                                    left: tooltipPosition.left,
                                                                                    top: tooltipPosition.top
                                                                                }}
                                                                        >
                                                                            <table>
                                                                                <thead className='bg-eventDetail-primary border-b-2 border-b-eventDetail-sbv'>
                                                                                <tr>
                                                                                    <th className='px-3'>Spread</th>
                                                                                    {outcomeIds.map((outcomeId) => (
                                                                                            <th key={`tooltip-head-${marketItem.id}-${outcomeId}`}
                                                                                                className='px-3'>
                                                                                                {marketItem.outcomes[outcomeId].name}
                                                                                            </th>
                                                                                    ))}
                                                                                </tr>
                                                                                </thead>
                                                                                <tbody className='bg-eventDetail-sbv-row'>
                                                                                {Object.entries(spreads)
                                                                                        .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
                                                                                        .map(([spread, odds]) => (
                                                                                                <tr key={`tooltip-row-${bookmaker.id}-${marketItem.id}-${spread}`}
                                                                                                    className='border-b-2 border-b-eventDetail-sbv'>
                                                                                                    <td className='col-1 text-table-multisbv'>{spread}</td>
                                                                                                    {outcomeIds.map((outcomeId) => (
                                                                                                            <td key={`tooltip-cell-${bookmaker.id}-${marketItem.id}-${spread}-${outcomeId}`}
                                                                                                                className='col-1'>
                                                                                                                {odds[outcomeId]}
                                                                                                            </td>
                                                                                                    ))}
                                                                                                </tr>
                                                                                        ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                ) : null}
                                                            </Fragment>
                                                    ) : null}
                                                    {value == null ? (
                                                            <span className={emptyColor}>-</span>
                                                    ) : isTop ? (
                                                            <span
                                                                    className={`inline-flex min-w-[40px] items-center justify-center rounded-md border border-sky-500/40 bg-sky-500/20 px-1 py-0.5 font-bold text-white ${isClickable ? "cursor-pointer" : ""}`}
                                                                    onClick={onOddClick ?? undefined}
                                                            >
                                                                {valueNode}
                                                            </span>
                                                    ) : (
                                                            <span
                                                                    className={`font-semibold text-white ${isClickable ? "cursor-pointer" : ""}`}
                                                                    onClick={onOddClick ?? undefined}
                                                            >
                                                                {valueNode}
                                                            </span>
                                                    )}
                                                </td>
                                        );
                                    });
                                })}
                            </tr>
                    );
                })}
                </tbody>
            </table>
    )
}
